import React from "react";
import {
  Activity,
  Clapperboard,
  Crown,
  FileText,
  FolderOpen,
  Headphones,
  Image,
  Info,
  KeyRound,
  Languages,
  LayoutDashboard,
  LogIn,
  Menu,
  Mic,
  Music,
  Music2,
  Radio,
  Receipt,
  ScrollText,
  Settings,
  ShieldCheck,
  Sparkles,
  Type,
  User as UserIcon,
  Users,
  Wallet,
  Zap,
} from "lucide-react";

type LucideIconComponent = React.ComponentType<{
  size?: number | string;
  className?: string;
  strokeWidth?: number | string;
}>;

export type MenuSvgIconCode =
  | "folder-open"
  | "zap"
  | "music"
  | "sparkles"
  | "type"
  | "mic"
  | "languages"
  | "headphones"
  | "image"
  | "radio"
  | "clapperboard"
  | "layout-dashboard"
  | "settings"
  | "users"
  | "shield-check"
  | "music-2"
  | "menu"
  | "scroll-text"
  | "info"
  | "folder"
  | "podcast"
  | "layout-grid"
  | "sliders-horizontal"
  | "list"
  | "crown"
  | "user"
  | "key-round"
  | "file-text"
  | "log-in"
  | "activity"
  | "wallet"
  | "receipt";

type MenuSvgIconDef = {
  code: MenuSvgIconCode;
  label: string;
  Icon: LucideIconComponent;
};

const exactHtmlIconList = [
  { code: "folder-open", label: "\u7cbe\u54c1\u8bd1\u5236", Icon: FolderOpen },
  { code: "zap", label: "\u4e00\u952e\u901f\u8bd1", Icon: Zap },
  { code: "music", label: "\u97f3\u8272\u5e93", Icon: Music },
  { code: "sparkles", label: "\u97f3\u8272\u8bbe\u8ba1", Icon: Sparkles },
  { code: "type", label: "\u6587\u672c\u8f6c\u8bed\u97f3", Icon: Type },
  { code: "mic", label: "\u97f3\u8272\u514b\u9686", Icon: Mic },
  { code: "languages", label: "\u5b57\u5e5f\u7ffb\u8bd1", Icon: Languages },
  { code: "headphones", label: "AI\u97f3\u9891", Icon: Headphones },
  { code: "image", label: "AI\u751f\u56fe", Icon: Image },
  { code: "radio", label: "AI\u64ad\u5ba2", Icon: Radio },
  { code: "clapperboard", label: "AI\u89c6\u9891", Icon: Clapperboard },
  {
    code: "layout-dashboard",
    label: "\u5267\u96c6\u7ba1\u7406",
    Icon: LayoutDashboard,
  },
  { code: "settings", label: "\u7cfb\u7edf\u7ba1\u7406", Icon: Settings },
  { code: "users", label: "\u7528\u6237\u7ba1\u7406", Icon: Users },
  {
    code: "shield-check",
    label: "\u89d2\u8272\u7ba1\u7406",
    Icon: ShieldCheck,
  },
  { code: "music-2", label: "\u97f3\u8272\u7ba1\u7406", Icon: Music2 },
  { code: "menu", label: "\u83dc\u5355\u7ba1\u7406", Icon: Menu },
  { code: "scroll-text", label: "\u65e5\u5fd7\u7ba1\u7406", Icon: ScrollText },
  { code: "info", label: "\u57fa\u7840\u4fe1\u606f", Icon: Info },
  { code: "crown", label: "\u4f1a\u5458\u4e0e\u79ef\u5206", Icon: Crown },
  { code: "user", label: "\u4e2a\u4eba\u8d44\u6599", Icon: UserIcon },
  { code: "key-round", label: "API \u5bc6\u94a5", Icon: KeyRound },
  { code: "file-text", label: "API \u6587\u6863", Icon: FileText },
  { code: "log-in", label: "\u767b\u9646\u65e5\u5fd7", Icon: LogIn },
  { code: "activity", label: "\u64cd\u4f5c\u65e5\u5fd7", Icon: Activity },
  { code: "wallet", label: "\u8d26\u6237\u4e0e\u53d1\u7968", Icon: Wallet },
  { code: "receipt", label: "\u53d1\u7968\u7ba1\u7406", Icon: Receipt },
] as const satisfies readonly MenuSvgIconDef[];

const legacyIconAliases = [
  { code: "folder", label: "\u7cbe\u54c1\u8bd1\u5236", Icon: FolderOpen },
  { code: "podcast", label: "AI\u64ad\u5ba2", Icon: Radio },
  {
    code: "layout-grid",
    label: "\u5267\u96c6\u7ba1\u7406",
    Icon: LayoutDashboard,
  },
  {
    code: "sliders-horizontal",
    label: "\u97f3\u8272\u7ba1\u7406",
    Icon: Music2,
  },
  { code: "list", label: "\u83dc\u5355\u7ba1\u7406", Icon: Menu },
] as const satisfies readonly MenuSvgIconDef[];

export const menuSvgIconList: readonly MenuSvgIconDef[] = Object.freeze([
  ...exactHtmlIconList,
]);

export const menuSvgIconMap = new Map<MenuSvgIconCode, MenuSvgIconDef>(
  [...exactHtmlIconList, ...legacyIconAliases].map((icon) => [icon.code, icon]),
);

export function isMenuSvgIconCode(
  code?: string | null,
): code is MenuSvgIconCode {
  return !!code && menuSvgIconMap.has(code as MenuSvgIconCode);
}

export function MenuSvgIcon({
  code,
  size = 16,
  className,
}: {
  code: string;
  size?: number;
  className?: string;
}) {
  const icon = menuSvgIconMap.get(code as MenuSvgIconCode);
  if (!icon) return null;
  const Icon = icon.Icon;
  return <Icon className={className} size={size} strokeWidth={2} />;
}
